import styled from 'styled-components';
import { media } from '@bitcoin-portal/bitcoincom-pkg-theme';
import Section from '@bitcoin-portal/bitcoincom-pkg-components/dist/Section';
import { LazyLoadImage } from 'react-lazy-load-image-component';

interface SliderProps {
  percentageValue: number;
}

interface ButtonGroupProps {
  active: boolean;
}

interface BitcoinPagesProps {
  hasBitcoinPages: boolean;
}

interface FUSDPageProps {
  hasFUSDPage: boolean;
}

export const StyledSection = styled(Section)`
  background: #f0f0f5 !important;
  padding: 40px 0px !important;
  margin: 0px !important;

  ${media.md`
    padding: 80px 0px !important;
  `}

  ${media.xl`
    padding: 100px 0px !important;
  `}
`;

export const StyledContentBlock = styled.div`
  max-width: 1440px;
  padding: 0px !important;
  margin: auto;
`;

export const MainContainer = styled.div`
  padding: 0px 20px !important;

  ${media.md`
    padding: 0px 60px !important;
  `}

  ${media.xl`
    padding: 0px 80px !important;
  `}
`;

export const MainTitle = styled.h2`
  display: none;
  color: #1c1c1c;
  font-family: 'Elza Condensed';
  margin: 0px;

  ${media.md`
    display: flex;
    justify-content: center;
    text-align: center;
    font-size: 70px;
    font-weight: 500;
    line-height: 85%;
    letter-spacing: -1.4px;
    text-transform: uppercase;
  `}

  ${media.xl`
    font-size: 90px;
    font-weight: 500;
    line-height: 87%;
    letter-spacing: -1.8px;
  `}
`;

export const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  width: 100%;
  gap: 10px;

  ${media.xl`
    gap: 20px;
  `}
`;

export const MainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 10px;
  margin: 0px;

  ${media.md`
    display: none;
  `}

  ${media.xl`
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: 20px;
    margin: 50px 0px 0px;
  `}
`;

export const FirstColumn = styled.div<FUSDPageProps>`
  display: flex;
  flex-direction: column;
  justify-content: ${props => (props.hasFUSDPage ? 'center' : 'space-between')};
  align-items: start;
  align-self: stretch;
  background: #ffffff;
  width: calc(100% - 40px);
  gap: 16px;
  padding: 20px;
  border-radius: 10px;

  ${media.md`
    gap: 20px;
    padding: 20px;
    border-radius: 10px;
  `}

  ${(props) => media.md`
    flex-direction: ${props.hasFUSDPage ? 'column' : 'row'};
    width: ${props.hasFUSDPage ? 'calc(50% - 45px)' : 'calc(100% - 40px)'};
  `}

  ${media.xl`
    flex-direction: column;
    gap: 25px;
    padding: 30px;
    border-radius: 20px;
  `}

  ${(props) => media.xl`
    width: ${props.hasFUSDPage ? 'calc(50% - 70px)' : 'calc(33.33% - 73.33px)'};
  `}
`;

export const BitcoinTitle = styled.h3<FUSDPageProps>`
  color: #1c1c1c;
  font-family: 'Elza Condensed';
  width: 100%;
  font-size: 50px;
  font-weight: 500;
  line-height: 85%;
  letter-spacing: -1px;
  text-transform: uppercase;
  margin: 0px;

  ${media.md`
    font-size: 44px;
    font-weight: 500;
    line-height: 85%;
    letter-spacing: -0.88px;
  `}

  ${(props) => media.md`
    width: ${props.hasFUSDPage ? '100%' : 'calc(50% - 10px)'};
  `}

  ${media.xl`
    width: 100%;
    font-size: 70px;
    font-weight: 500;
    line-height: 85%;
    letter-spacing: -1.4px;
  `}

  @media (min-width: 1200px) {
    html[lang='ru'] &,
    html[lang='uk'] & {
      font-size: 60px !important;
    }
  }
`;

export const BitcoinText = styled.p<FUSDPageProps>`
  color: #6b6d7b;
  font-family: 'Satoshi Variable Medium';
  width: 100%;
  font-size: 16px;
  font-weight: 500;
  line-height: 135%;
  letter-spacing: -0.32px;
  margin: 0px;

  ${media.md`
    font-size: 16px;
    font-weight: 500;
    line-height: 135%;
    letter-spacing: -0.32px;
  `}

  ${(props) => media.md`
    width: ${props.hasFUSDPage ? '100%' : 'calc(50% - 10px)'};
  `}

  ${media.xl`
    width: 100%;
    font-size: 20px;
    font-weight: 500;
    line-height: 130%;
    letter-spacing: normal;
  `}
`;

export const SecondColumn = styled.div<FUSDPageProps>`
  display: flex;
  flex-direction: column;
  align-items: start;
  width: 100%;
  gap: 10px;

  ${media.md`
    gap: 10px;
  `}

  ${(props) => media.md`
    width: ${props.hasFUSDPage ? '100%' : 'calc(50% - 5px)'};
    justify-content: ${props.hasFUSDPage ? 'space-between' : 'start'};
  `}

  ${media.xl`
    gap: 20px;
  `}

  ${(props) => media.xl`
    width: ${props.hasFUSDPage ? 'calc(50% - 10px)' : 'calc(33.33% - 13.33px)'};
  `}
`;

export const BitcoinPriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: #ffffff;
  width: calc(100% - 40px);
  height: 128px;
  gap: 10px;
  padding: 20px;
  border-radius: 10px;

  ${media.md`
    width: calc(100% - 40px);
    height: 123px;
    gap: 10px;
    padding: 20px;
    border-radius: 10px;
  `}

  ${media.xl`
    width: calc(100% - 60px);
    height: 123px;
    gap: 20px;
    padding: 30px;
    border-radius: 20px;
  `}
`;

export const PriceTitle = styled.h3`
  font-family: 'Satoshi Variable Medium';
  color: #6b6d7b;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 135%;
  letter-spacing: -0.32px;
  margin: 0px;

  ${media.md`
    font-size: 16px;
    font-weight: 500;
    line-height: 135%;
    letter-spacing: -0.32px;
  `}

  ${media.xl`
    font-size: 20px;
    font-weight: 500;
    line-height: 130%;
    letter-spacing: normal;
  `}
`;

export const PriceContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  ${media.md`
    gap: 10px;
  `}

  ${media.xl`
    gap: 20px;
  `}
`;

export const PriceText = styled.h3`
  color: #1c1c1c;
  font-family: 'Elza Condensed';
  font-size: 60px;
  font-weight: 500;
  line-height: 80%;
  letter-spacing: -1.4px;
  text-transform: uppercase;
  margin: 0px;

  ${media.md`
    font-size: 70px;
    font-weight: 500;
    line-height: 85%;
    letter-spacing: -1.4px;
  `}

  ${media.xl`
    font-size: 70px;
    font-weight: 500;
    line-height: 85%;
    letter-spacing: -1.4px;
  `}

  @media (min-width: 1200px) {
    html[lang='ru'] &,
    html[lang='uk'] & {
      font-size: 60px !important;
    }
  }

  @media (min-width: 768px) and (max-width: 991px) {
    html[lang='ru'] &,
    html[lang='uk'] & {
      font-size: 60px !important;
    }
  }
`;

export const GainOrLossTextComparison = styled.p`
  font-family: 'Chivo Mono', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 130%;
  width: fit-content;
  margin: 0px;

  ${media.xl`
    font-size: 16px;
    font-weight: 600;
    line-height: normal;
  `}

  span {
    display: flex;
    align-items: center;
    padding: 5px 10px;
    border-radius: 50px;
  }

  .positive {
    color: #e6f7f4;
    background: #2aa5a5;
  }

  .negative {
    color: #e84142;
    background: #fcd7cc;
  }
`;

export const GraphContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: #ffffff;
  width: calc(100% - 40px);
  height: 293px;
  gap: 10px;
  padding: 20px;
  border-radius: 10px;

  ${media.md`
    width: calc(100% - 40px);
    height: 272px;
    gap: 20px;
    padding: 20px;
    border-radius: 10px;
  `}

  ${media.lg`
    width: calc(100% - 40px);
    height: 330px;
    gap: 20px;
    padding: 20px;
    border-radius: 10px;
  `}

  ${media.xl`
    width: calc(100% - 60px);
    height: 310px;
    gap: 20px;
    padding: 30px;
    border-radius: 20px;
  `}
`;

export const GraphTitle = styled.h3`
  color: #6b6d7b;
  font-family: 'Satoshi Variable Medium';
  font-size: 16px;
  font-weight: 500;
  line-height: 135%;
  letter-spacing: -0.32px;
  margin: 0px;

  ${media.md`
    font-size: 16px;
    font-weight: 500;
    line-height: 135%;
    letter-spacing: -0.32px;
  `}

  ${media.xl`
    font-size: 20px;
    font-weight: 500;
    line-height: 130%;
    letter-spacing: normal;
  `}
`;

export const GraphImage = styled(LazyLoadImage)`
  display: flex;
  justify-content: end;
  align-items: center;
  align-self: stretch;
`;

export const ThirdColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: start;
  align-self: stretch;
  background: #ffffff;
  width: calc(100% - 40px);
  padding: 20px;
  border-radius: 10px;

  ${media.md`
    width: calc(50% - 45px);
    padding: 20px;
    border-radius: 10px;
  `}

  ${media.xl`
    width: calc(33.33% - 73.33px);
    padding: 30px;
    border-radius: 20px;
  `}
`;

export const PriceComparisonContaniner = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: start;
  width: 100%;
  gap: 25px;

  ${media.md`
    gap: 20px;
  `}

  ${media.xl`
    gap: 25px;
  `}
`;

export const PriceComparisonContentContaniner = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: start;
  text-align: start;
`;

export const PriceComparisonContentWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

export const BitcoinImage = styled(LazyLoadImage)`
  width: 52px;
  height: 56px;

  ${media.md`
    width: 45px;
    height: 48px;
  `}

  ${media.xl`
    width: 59px;
    height: 63px;
  `}
`;

export const PriceComparisonTitle = styled.h3`
  color: #000000;
  font-family: 'Satoshi Variable Medium';
  font-size: 30px;
  font-weight: 700;
  line-height: 109%;
  letter-spacing: normal;
  margin: 0px;

  ${media.md`
    font-size: 32px;
    font-weight: 700;
    line-height: 105%;
    letter-spacing: -0.64px;
  `}

  ${media.xl`
    font-size: 30px;
    font-weight: 700;
    line-height: 109%;
    letter-spacing: normal;
  `}
`;

export const PriceComparisonText = styled.p`
  font-family: 'Satoshi Variable Medium';
  color: #6b6d7b;
  font-size: 16px;
  font-weight: 500;
  line-height: 135%;
  letter-spacing: -0.32px;
  margin: 9px 0px 0px;

  ${media.md`
    font-size: 16px;
    font-weight: 500;
    line-height: 135%;
    letter-spacing: -0.32px;
    margin: 7px 0px 0px;
  `}

  ${media.xl`
    font-size: 20px;
    font-weight: 500;
    line-height: 130%;
    letter-spacing: normal;
    margin: 9px 0px 0px;
  `}
`;

export const Text = styled.p`
  font-family: 'Satoshi Variable Medium';
  color: #6b6d7b;
  font-size: 16px;
  font-weight: 500;
  line-height: 135%;
  letter-spacing: -0.32px;
  margin: 3px 0px 0px;

  ${media.md`
    font-size: 16px;
    font-weight: 500;
    line-height: 135%;
    letter-spacing: -0.32px;
    margin: 2px 0px 0px;
  `}

  ${media.xl`
    font-size: 20px;
    font-weight: 500;
    line-height: 130%;
    letter-spacing: normal;
    margin: 3px 0px 0px;
  `}
`;

export const PriceContentWrapperComparison = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
  gap: 20px;

  ${media.xl`
    gap: 16px;
  `}

  ${media.xl`
    gap: 20px;
  `}
`;

export const PriceTextComparison = styled.p`
  color: #1c1c1c;
  font-family: 'Elza Condensed';
  font-size: 48px;
  font-weight: 500;
  line-height: 80%;
  letter-spacing: -1.4px;
  text-transform: uppercase;
  margin: 0px;

  ${media.md`
    font-size: 48px;
    font-weight: 500;
    line-height: 85%;
    letter-spacing: -1.4px;
  `}

  ${media.lg`
    font-size: 60px;
    font-weight: 500;
    line-height: 85%;
    letter-spacing: -1.4px;
  `}
`;

export const SliderContainer = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

export const Slider = styled.input<SliderProps>`
  -webkit-appearance: none;
  appearance: none;
  width: 85%;
  height: 10px;
  background: linear-gradient(
    to right,
    #ff9500 ${props => props.percentageValue}%,
    #f0f0f5 ${props => props.percentageValue}% 100%
  );
  border-radius: 5px;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    background: #ff9500;
    border-radius: 50%;
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    background: #ff9500;
    border-radius: 50%;
    cursor: pointer;
  }

  ${media.md`
    width: 80%;
  `}
`;

export const SliderValue = styled.span`
  color: #1c1c1c;
  font-family: 'Satoshi Variable Medium';
  width: 15%;
  font-size: 14px;
  font-weight: 500;
  line-height: 130%;
  margin: 0px;
  text-align: right;

  ${media.md`
    width: 20%;
    font-size: 18px;
    font-weight: 500;
    line-height: 123%;
  `}

  ${media.xl`
    font-size: 22px;
    font-weight: 500;
    line-height: 123%;
  `}
`;

export const ButtonWrapper = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 10px;

  ${media.md`
    gap: 8px;
  `}

  ${media.xl`
    gap: 10px;
  `}
`;

export const ButtonGroup = styled.button<ButtonGroupProps>`
  background: ${props => (props.active ? '#1c1c1c' : '#ffffff')};
  font-family: 'Chivo Mono', sans-serif;
  color: ${props => (props.active ? '#ffffff' : '#693000')};
  font-size: 12px;
  font-weight: 500;
  line-height: 109%;
  text-transform: uppercase;
  padding: ${props => (props.active ? '5px 10px' : '3px 10px')};
  border: ${props => (props.active ? 'none' : '2px solid #693000')};
  border-radius: 103.546px;
  cursor: pointer;

  ${media.md`
    font-size: 10px;
    font-weight: 500;
    line-height: 130%;
  `}

  ${media.xl`
    font-size: 12px;
    font-weight: 500;
    line-height: 130%;
  `}
`;

export const BitcoinButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  gap: 8px;

  ${media.md`
    gap: 10px;
  `}

  ${media.xl`
    gap: 10px;
  `}
`;

export const BitcoinButton1 = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  width: calc(100% - 56px);
  background: #ff9500;
  border-radius: 116.162px;
  padding: 14px 28px;
  gap: 9.293px;
  height: 20px;
  color: #1c1c1c;
  font-family: 'Satoshi Variable Medium';
  font-size: 20px;
  font-weight: 500;
  line-height: 110%;
  margin: 0px;
  text-align: center;
  cursor: pointer;

  position: relative;
  overflow: hidden;
  z-index: 1;
  transition: background 0.3s ease, color 0.3s ease;

  &:hover {
    background: #ff7c18;
  }

  .ripple {
    position: absolute;
    border-radius: 50%;
    transform: scale(0);
    animation: ripple-animation 1.5s ease-out;
    background-color: rgba(204, 85, 0, 0.6);
    pointer-events: none;
  }

  @keyframes ripple-animation {
    to {
      transform: scale(4);
      opacity: 0;
    }
  }

  ${media.xl`
    height: 28px;
  `}
`;

export const BitcoinButton2 = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  width: calc(100% - 56px);
  background: #1c1c1c;
  border-radius: 116.162px;
  padding: 14px 28px;
  gap: 9.293px;
  height: 20px;
  color: #ffffff;
  font-family: 'Satoshi Variable Medium';
  font-size: 20px;
  font-weight: 500;
  line-height: 110%;
  margin: 0px;
  text-align: center;
  cursor: pointer;

  position: relative;
  overflow: hidden;
  z-index: 1;
  transition: background 0.3s ease, color 0.3s ease;

  &:hover {
    background: #434248;
  }

  .ripple {
    position: absolute;
    border-radius: 50%;
    transform: scale(0);
    animation: ripple-animation 2s ease-out;
    background-color: rgba(255, 255, 255, 0.4);
    pointer-events: none;
  }

  @keyframes ripple-animation {
    to {
      transform: scale(4);
      opacity: 0;
    }
  }

  ${media.xl`
    height: 28px;
  `}
`;

export const MainWrapperTablet = styled.div<FUSDPageProps>`
  display: none;

  ${media.md`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    align-items: stretch;
    gap: 10px;
    margin: 40px 0px 0px;
  `}

  ${(props) => media.md`
    flex-direction: ${props.hasFUSDPage ? 'row' : 'column'};
  `}

  ${media.xl`
    display: none;
  `}
`;

export const ColumnWrapper = styled.div<FUSDPageProps>`
  display: none;

  ${media.md`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    align-items: stretch;
    gap: 10px;
  `}

  ${(props) => media.md`
    width: ${props.hasFUSDPage ? 'calc(50% - 5px)' : '100%'};
  `}

  ${media.xl`
    display: none;
  `}
`;

export const CardSection = styled.div<BitcoinPagesProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 10px;

  ${media.xl`
    gap: 20px;
  `}

  ${(props) => media.xl`
    flex-direction: ${props.hasBitcoinPages ? 'row' : 'column'};
  `}
`;

export const Card = styled.div<BitcoinPagesProps>`
  display: flex;
  justify-content: start;
  align-items: center;
  align-self: stretch;
  background: #ffffff;
  width: calc(100% - 40px);
  gap: 10px;
  padding: 20px;
  border-radius: 10px;

  ${(props) => media.xl`
    width: ${props.hasBitcoinPages ? 'calc(33.33% - 53.33px)' : 'calc(100% - 60px)'};
    padding: ${props.hasBitcoinPages ? '20px' : '30px'};
  `};
`;

export const CardIcon = styled(LazyLoadImage)`
  width: 56px;
  height: 56px;

  ${media.xl`
    width: 62px;
    height: 62px;
  `}
`;

export const CardTitle = styled.h3`
  color: #1c1c1c;
  font-family: 'Elza Condensed';
  font-size: 24px;
  font-weight: 500;
  line-height: 95%;
  letter-spacing: -0.46px;
  text-transform: uppercase;
  margin: 0px;

  ${media.md`
    font-size: 24px;
    font-weight: 500;
    line-height: 95%;
    letter-spacing: -0.46px;
  `}

  ${media.xl`
    font-size: 34px;
    font-weight: 500;
    line-height: 89%;
    letter-spacing: -0.68px;
  `}
`;
